import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Collections as LibraryIcon,
} from '@mui/icons-material';
import { useSites } from '../../hooks/useSites';
import { Page } from '../../types';
import { useImageUpload } from '../../hooks/useImageUpload';
import { ImageLibraryModal } from './ImageLibraryModal';
import { ImageMetadata, ImageService } from '../../services/imageService';
import { PageEditDialog } from './pageEditor';
import { 
  PageSlotCard, 
  BackgroundSettingsSection, 
  GridSizeControls, 
  PageGrid 
} from './pageConfiguration';
import { ConfirmDialog } from './common';
import { PAGE_CONFIG, PAGE_MESSAGES, FORM_CONFIG, getColumnLetter } from './pageConfiguration/constants';
import { validateImageFile, createLocalImageUrl, revokeLocalImageUrl, getImageFileFromDrop } from './pageConfiguration/utils';
import { PageSlot, AppliedContentMap } from '../../types/pageConfiguration';

// PageSlot型はpageConfiguration.tsからインポート済み

interface PageConfigurationTabProps {
  editState?: any;
  updateEditState?: (updates: any) => void;
  siteId?: string;
  onAppliedContentChange?: (appliedContent: {[pageId: string]: {html: string, css: string}}) => void;
  cachedImages?: ImageMetadata[];
  preloaded?: boolean;
  /** 新規作成時: ページ画像ファイルを一時保存（保存ボタンでアップロード） */
  onPendingPageImageFile?: (slotId: string, file: File | null) => void;
  /** 新規作成時: 背景画像ファイルを一時保存 */
  onPendingBackgroundImageFile?: (file: File | null) => void;
  /** 新規作成時: 横スライド画像を一時保存 key=${pageId}-${slideId} */
  onPendingSlideImageFile?: (slideKey: string, file: File | null) => void;
}

export const PageConfigurationTab: React.FC<PageConfigurationTabProps> = ({ 
  editState, 
  updateEditState, 
  siteId,
  onAppliedContentChange,
  cachedImages = [],
  preloaded = false,
  onPendingPageImageFile,
  onPendingBackgroundImageFile,
  onPendingSlideImageFile,
}) => {
  const { uploadImage } = useImageUpload();
  const { updateSite } = useSites();
  
  // 適用済みのHTML/CSS内容を管理する状態
  const [appliedCustomContent, setAppliedCustomContent] = React.useState<{[pageId: string]: {html: string, css: string}}>({});
  
  // 適用済みの内容を更新する関数
  const updateAppliedContent = (pageId: string, html: string, css: string) => {
    setAppliedCustomContent(prev => ({
      ...prev,
      [pageId]: { html, css }
    }));
  };
  
  // 適用済みの内容を取得する関数
  const getAppliedContent = (pageId: string) => {
    return appliedCustomContent[pageId] || { html: '', css: '' };
  };
  
  // 適用済み内容が変更された時に親コンポーネントに通知
  React.useEffect(() => {
    if (onAppliedContentChange) {
      onAppliedContentChange(appliedCustomContent);
    }
  }, [appliedCustomContent, onAppliedContentChange]);
  
  // getColumnLetter関数はconstants.tsからインポート済み
  
  const [pageSlots, setPageSlots] = useState<PageSlot[]>([]);
  const initializedRef = useRef(false);
  const [draggedSlot, setDraggedSlot] = useState<PageSlot | null>(null);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [uploadingSlots, setUploadingSlots] = useState<Set<string>>(new Set());
  const [tempRow, setTempRow] = useState(editState?.maxRows || 1);
  const [tempColumn, setTempColumn] = useState(editState?.maxColumns || 1);
  const [showWarning, setShowWarning] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<PageSlot | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [pageImageLibraryModalOpen, setPageImageLibraryModalOpen] = useState(false);
  const [pageImageMenuAnchor, setPageImageMenuAnchor] = useState<HTMLElement | null>(null);
  const pageImageFileInputRef = useRef<HTMLInputElement>(null);
  const [backgroundImageLibraryModalOpen, setBackgroundImageLibraryModalOpen] = useState(false);
  const [backgroundImageMenuAnchor, setBackgroundImageMenuAnchor] = useState<HTMLElement | null>(null);
  const backgroundImageFileInputRef = useRef<HTMLInputElement>(null);
  const [libraryImages, setLibraryImages] = useState<ImageMetadata[]>([]);
  const [libraryVideos, setLibraryVideos] = useState<ImageMetadata[]>([]);
  const [libraryImagesLoading, setLibraryImagesLoading] = useState(false);
  
  // editStateが変更された時にtempRowとtempColumnを更新
  useEffect(() => {
    if (editState?.maxRows) {
      setTempRow(editState.maxRows);
    }
    if (editState?.maxColumns) {
      setTempColumn(editState.maxColumns);
    }
  }, [editState?.maxRows, editState?.maxColumns]);

  // pageBackgroundColorが未設定の場合、デフォルト値#ffffffを設定
  useEffect(() => {
    if (editState && updateEditState) {
      // settingsが存在するが、pageBackgroundColorが未設定の場合
      if (editState.settings && editState.settings.pageBackgroundColor === undefined) {
        updateEditState({
          settings: {
            ...editState.settings,
            pageBackgroundColor: '#ffffff'
          }
        });
      }
      // settingsが存在しない場合、settingsオブジェクトを作成してpageBackgroundColorを設定
      else if (!editState.settings) {
        updateEditState({
          settings: {
            pageBackgroundColor: '#ffffff'
          }
        });
      }
    }
  }, [editState?.settings?.pageBackgroundColor, updateEditState]);
  
  // editStateが変更された時にpageSlotsを初期化
  useEffect(() => {
    if (editState && editState.maxRows && editState.maxColumns && !initializedRef.current && pageSlots.length === 0) {
      const slots: PageSlot[] = [];
      // maxRowsとmaxColumnsに基づいてスロットを生成
      const maxRows = editState.maxRows;
      const maxColumns = editState.maxColumns;
      
      for (let row = 1; row <= maxRows; row++) {
        for (let col = 1; col <= maxColumns; col++) {
          const columnLetter = getColumnLetter(col);
          const slotId = `${row}-${columnLetter}`;
          
          // 既存のページデータがあるかチェック
          const existingPage = editState.pages?.find((page: Page) => {
            // page.idから行と列を抽出（例: "page-1-A" -> row: 1, column: "A"）
            const match = page.id?.match(/page-(\d+)-([A-Z]+)/);
            if (match) {
              const pageRow = parseInt(match[1]);
              const pageColumn = match[2];
              return pageRow === row && pageColumn === columnLetter;
            }
            return false;
          });
        
          slots.push({
              id: slotId,
              row,
              column: columnLetter,
              page: existingPage,
              isEmpty: !existingPage
          });
        }
      }
    
      setPageSlots(slots);
      initializedRef.current = true;
    }
  }, [editState?.maxRows, editState?.maxColumns, editState?.pages]);
  
  // siteIdが変更された時に初期化フラグをリセット
  useEffect(() => {
    initializedRef.current = false;
  }, [siteId]);

  // 統一された状態管理システムへの反映（debounce付き）
  // 初期化中は無効化して循環を防ぐ
  useEffect(() => {
    // 初期化が完了していない場合は、強制的に初期化完了とする
    if (!initializedRef.current && pageSlots.length > 0) {
      initializedRef.current = true;
    }
    
    if (updateEditState && initializedRef.current) {
      const timeoutId = setTimeout(() => {
        // pageSlotsからページデータを抽出
        const pages = pageSlots
          .filter(slot => slot.page && !slot.isEmpty)
          .map(slot => {
            // 列の文字を数字に変換する関数
            const getColumnNumber = (column: string): number => {
              if (column.length === 1) {
                return column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
              }
              const first = column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
              const second = column.charCodeAt(1) - 'A'.charCodeAt(0) + 1;
              return 26 + (first - 1) * 26 + second;
            };
            
            return {
              ...slot.page!,
              row: slot.row,
              column: slot.column
            };
          });
        
        // 現在のeditStateと比較して変更がある場合のみ更新
        const currentPages = editState?.pages || [];
        const hasChanges = JSON.stringify(pages) !== JSON.stringify(currentPages) || 
                          JSON.stringify(pageSlots) !== JSON.stringify(editState?.pageSlots);
        
        if (hasChanges) {
          updateEditState({ 
            pageSlots, 
            pages 
          });
        }
      }, 100); // 100msのdebounce
      
      return () => clearTimeout(timeoutId);
    }
  }, [pageSlots]);

  const handleClearAll = () => {
    if (siteId === 'create') {
      pageSlots.forEach(slot => onPendingPageImageFile?.(slot.id, null));
    }
    setPageSlots(pageSlots.map(slot => ({ ...slot, page: undefined, isEmpty: true })));
    setValidationError(null);
  };

  // editState.pagesが外部から更新された場合にpageSlotsを同期
  useEffect(() => {
    if (editState?.pages && pageSlots.length > 0) {
      setPageSlots(prevSlots => {
        return prevSlots.map(slot => {
          if (slot.page) {
            // editState.pagesから対応するページを探す
            const updatedPage = editState.pages.find((p: any) => 
              p.id === slot.page?.id || 
              (p.row === slot.row && p.column === slot.column)
            );
            
            // ページがeditState.pagesから削除された場合
            if (!updatedPage) {
              return {
                ...slot,
                page: undefined,
                isEmpty: true
              };
            }
            
            // 背景画像が変更された場合
            if (updatedPage.backgroundImage !== slot.page.backgroundImage) {
              // 背景画像がなくなった場合はページを削除
              if (!updatedPage.backgroundImage) {
                return {
                  ...slot,
                  page: undefined,
                  isEmpty: true
                };
              } else {
                // 背景画像が変更された場合は更新
                return {
                  ...slot,
                  page: {
                    ...slot.page,
                    backgroundImage: updatedPage.backgroundImage
                  }
                };
              }
            }
          }
          return slot;
        });
      });
    }
  }, [editState?.pages]);

  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      // コンポーネントがアンマウントされる際のクリーンアップ
    };
  }, []);


  // ページの隣接性をチェックするバリデーション関数
  const validatePageAdjacency = (): { isValid: boolean; errorMessage: string | null } => {
    const pagesWithContent = pageSlots.filter(slot => slot.page && !slot.isEmpty);
    
    if (pagesWithContent.length === 0) {
      return { isValid: true, errorMessage: null };
    }

    // ページの位置をマップに変換
    const pageMap = new Map<string, boolean>();
    pagesWithContent.forEach(slot => {
      const key = `${slot.row}-${slot.column}`;
      pageMap.set(key, true);
    });

    // 各ページが隣接しているかチェック
    for (const slot of pagesWithContent) {
      const { row, column } = slot;
      const hasAdjacent = 
        // 左隣（A列以外）
        (column !== 'A' && pageMap.has(`${row}-${String.fromCharCode(column.charCodeAt(0) - 1)}`)) ||
        // 右隣（D列以外）
        (column !== 'D' && pageMap.has(`${row}-${String.fromCharCode(column.charCodeAt(0) + 1)}`)) ||
        // 上隣
        (row > 1 && pageMap.has(`${row - 1}-${column}`)) ||
        // 下隣
        pageMap.has(`${row + 1}-${column}`);

      if (!hasAdjacent) {
        return {
          isValid: false,
          errorMessage: `${row}行${column}列のページが他のページと隣接していません。すべてのページは隣接している必要があります。`
        };
      }
    }

    // A1が存在するかチェック
    if (!pageMap.has('1-A')) {
      return {
        isValid: false,
        errorMessage: 'A1にページが設定されていません。最初のページは必ずA1に配置してください。'
      };
    }

    return { isValid: true, errorMessage: null };
  };

  // 行を追加（下に追加）
  const handleAddRow = () => {
    const currentMaxRow = editState?.maxRows || 1;
    if (currentMaxRow >= PAGE_CONFIG.MAX_ROWS) {
      return;
    }

    const newMaxRow = currentMaxRow + 1;
    setTempRow(newMaxRow);

    // 必要なスロットを追加
    setPageSlots(prevSlots => {
      const newSlots = [...prevSlots];
      const currentMaxColumn = editState?.maxColumns || 1;
      
      for (let col = 1; col <= currentMaxColumn; col++) {
        const columnLetter = getColumnLetter(col);
        const slotId = `${newMaxRow}-${columnLetter}`;
        
        if (!newSlots.some(slot => slot.id === slotId)) {
          newSlots.push({
            id: slotId,
            row: newMaxRow,
            column: columnLetter,
            page: undefined,
            isEmpty: true
          });
        }
      }
      
      return newSlots;
    });

    // editStateを更新
    if (updateEditState) {
      updateEditState({
        maxRows: newMaxRow
      });
    }
  };

  // 列を追加（右に追加）
  const handleAddColumn = () => {
    const currentMaxColumn = editState?.maxColumns || 1;
    if (currentMaxColumn >= PAGE_CONFIG.MAX_COLUMNS) {
      return;
    }

    const newMaxColumn = currentMaxColumn + 1;
    setTempColumn(newMaxColumn);

    // 必要なスロットを追加
    setPageSlots(prevSlots => {
      const newSlots = [...prevSlots];
      const currentMaxRow = editState?.maxRows || 1;
      const newColumnLetter = getColumnLetter(newMaxColumn);
      
      for (let row = 1; row <= currentMaxRow; row++) {
        const slotId = `${row}-${newColumnLetter}`;
        
        if (!newSlots.some(slot => slot.id === slotId)) {
          newSlots.push({
            id: slotId,
            row,
            column: newColumnLetter,
            page: undefined,
            isEmpty: true
          });
        }
      }
      
      return newSlots;
    });

    // editStateを更新
    if (updateEditState) {
      updateEditState({
        maxColumns: newMaxColumn
      });
    }
  };

  // 行・列の変更を適用
  const handleApplyGridSize = () => {
    const newMaxRow = tempRow;
    const newMaxColumn = tempColumn;
    
    // 現在のページ数をチェック
    const currentPages = pageSlots.filter(slot => slot.page && !slot.isEmpty);
    const pagesToRemove = currentPages.filter(slot => {
      // 列の文字を数字に変換する関数
      const getColumnNumber = (column: string): number => {
        if (column.length === 1) {
          return column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
        }
        // 2文字の場合は、AA=27, AB=28...のように計算
        const first = column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
        const second = column.charCodeAt(1) - 'A'.charCodeAt(0) + 1;
        return 26 + (first - 1) * 26 + second;
      };
      
      const columnNumber = getColumnNumber(slot.column);
      return slot.row > newMaxRow || columnNumber > newMaxColumn;
    });
    
    if (pagesToRemove.length > 0) {
      setShowWarning(true);
      return;
    }
    
    // 必要なスロットを実際にpageSlotsに追加
    setPageSlots(prevSlots => {
      const newSlots = [...prevSlots];
      
      // 各行・各列のスロットを確認・追加
      for (let row = 1; row <= newMaxRow; row++) {
        for (let col = 1; col <= newMaxColumn; col++) {
          const columnLetter = getColumnLetter(col);
          const slotId = `${row}-${columnLetter}`;
          
          // 既存のスロットがない場合は追加
          if (!newSlots.some(slot => slot.id === slotId)) {
            newSlots.push({
                  id: slotId,
                  row,
                  column: columnLetter,
              page: undefined,
              isEmpty: true
            });
          }
        }
      }
      
      return newSlots;
    });
    
    // editStateを更新
    if (updateEditState) {
      updateEditState({
        maxRows: newMaxRow,
        maxColumns: newMaxColumn
      });
    }
    
    setShowWarning(false);
    setValidationError(null);
  };

  // 警告を無視して適用
  const handleForceApply = () => {
    const newMaxRow = tempRow;
    const newMaxColumn = tempColumn;
    
    // 範囲外のページを削除
    setPageSlots(prevSlots => {
      const getColumnNumber = (column: string): number => {
        if (column.length === 1) {
          return column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
        }
        const first = column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
        const second = column.charCodeAt(1) - 'A'.charCodeAt(0) + 1;
        return 26 + (first - 1) * 26 + second;
      };
      
      return prevSlots.filter(slot => {
        const columnNumber = getColumnNumber(slot.column);
        return slot.row <= newMaxRow && columnNumber <= newMaxColumn;
      });
    });
    
    // editStateを更新
    if (updateEditState) {
      updateEditState({
        maxRows: newMaxRow,
        maxColumns: newMaxColumn
      });
    }
    
    setShowWarning(false);
  };

  const handleImageSelect = (slotId: string, event: React.MouseEvent<HTMLElement>) => {
    setSelectedSlotId(slotId);
    setPageImageMenuAnchor(event.currentTarget);
  };

  // デバイスからページ画像をアップロード（新規時は一時保存のみ）
  const handlePageImageFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedSlotId) return;

    if (!ImageService.isSupportedImageType(file)) {
      alert('画像ファイルのみサポートされています（JPEG, PNG, WebP, GIF）');
      return;
    }
    if (!ImageService.isValidFileSize(file, 100)) {
      alert('ファイルサイズが大きすぎます（最大100MB）');
      return;
    }

    if (siteId === 'create' && onPendingPageImageFile) {
      onPendingPageImageFile(selectedSlotId, file);
      processImageFileCreateMode(file, selectedSlotId);
    } else if (siteId && siteId !== 'create') {
      processImageFile(file, selectedSlotId);
    }
    
    if (pageImageFileInputRef.current) {
      pageImageFileInputRef.current.value = '';
    }
    setPageImageMenuAnchor(null);
    setSelectedSlotId(null);
  };

  // ライブラリからページ画像が選択された時の処理
  const handlePageImageSelected = (imageUrl: string) => {
    if (selectedSlotId) {
      setPageSlots(prevSlots => {
        const updatedSlots = prevSlots.map(slot => {
          if (slot.id === selectedSlotId) {
            return {
              ...slot,
              page: buildPageWithUpdatedImage(slot, imageUrl),
              isEmpty: false
            };
          }
          return slot;
        });
        
        return updatedSlots;
      });
    }
    setPageImageLibraryModalOpen(false);
    setPageImageMenuAnchor(null);
    setSelectedSlotId(null);
  };

  // ImageLibraryModalから画像が選択された時の処理（背景画像用）
  const handleBackgroundImageSelected = (imageUrl: string) => {
    if (updateEditState) {
      updateEditState({
        settings: {
          ...editState?.settings,
          backgroundImage: imageUrl
        }
      });
    }
  };

  // ライブラリ画像を読み込む
  useEffect(() => {
    if (!siteId || siteId === 'create') return;

    if (preloaded && cachedImages.length > 0) {
      const imageOnly = cachedImages.filter(img => !img.isVideo);
      const videoOnly = cachedImages.filter(img => img.isVideo);
      setLibraryImages(imageOnly);
      setLibraryVideos(videoOnly);
      setLibraryImagesLoading(false);
      return;
    }

    let cancelled = false;
    setLibraryImagesLoading(true);
    (async () => {
      try {
        const images = await ImageService.getSiteImageLibrary(siteId);
        if (!cancelled) {
          const imageOnly = images.filter(img => !img.isVideo);
          const videoOnly = images.filter(img => img.isVideo);
          setLibraryImages(imageOnly);
          setLibraryVideos(videoOnly);
        }
      } catch (error) {
        console.error('画像の読み込みに失敗しました:', error);
      } finally {
        if (!cancelled) setLibraryImagesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, cachedImages, preloaded]);

  // 使用中の画像URLを集約
  const getUsedImageUrls = (): Set<string> => {
    const usedUrls = new Set<string>();
    
    if (!editState) return usedUrls;
    
    // ページの背景画像
    if (editState.pages && Array.isArray(editState.pages)) {
      editState.pages.forEach((page: any) => {
        if (page.backgroundImage) {
          usedUrls.add(page.backgroundImage);
        }
      });
    }
    
    // サイト設定の背景画像
    if (editState.settings?.backgroundImage) {
      usedUrls.add(editState.settings.backgroundImage);
    }
    
    return usedUrls;
  };

  // ページ削除モーダルを開く
  const handleDeletePage = (slot: PageSlot) => {
    if (!slot.page) return;
    setSlotToDelete(slot);
    setShowDeleteDialog(true);
  };

  // ページ削除を確定
  const confirmDeletePage = () => {
    if (!slotToDelete) return;
    if (siteId === 'create') onPendingPageImageFile?.(slotToDelete.id, null);
    setPageSlots(prevSlots => prevSlots.map(s =>
      s.id === slotToDelete.id ? { ...s, page: undefined, isEmpty: true } : s
    ));
    setShowDeleteDialog(false);
    setSlotToDelete(null);
  };

  const buildPageWithUpdatedImage = (slot: PageSlot, imageUrl: string) => ({
    ...(slot.page ?? {}),
    id: slot.page?.id || `page-${slot.row}-${slot.column}`,
    pageId: slot.page?.pageId || `${slot.row.toString().padStart(2, '0')}-${slot.column}`,
    row: slot.row,
    column: slot.column,
    type: slot.page?.type || ('vertical' as const),
    title: (slot.page as any)?.title || `ページ ${slot.row}-${slot.column}`,
    isHorizontal: slot.page?.isHorizontal ?? false,
    companyInfoVisible: slot.page?.companyInfoVisible ?? false,
    backgroundImage: imageUrl
  });

  // 新規作成時のみ: プレビュー表示のみ（アップロードは保存時に実行）
  const processImageFileCreateMode = (file: File, slotId: string) => {
    if (!file.type.startsWith('image/') || file.size > PAGE_CONFIG.MAX_FILE_SIZE) return;
    const localImageUrl = URL.createObjectURL(file);
    setPageSlots(prevSlots => prevSlots.map(slot => {
      if (slot.id !== slotId) return slot;
      return {
        ...slot,
        page: buildPageWithUpdatedImage(slot, localImageUrl),
        isEmpty: false
      };
    }));
  };

  // 画像ファイルを処理する共通関数
  const processImageFile = (file: File, slotId: string) => {
    if (siteId === 'create' && onPendingPageImageFile) {
      onPendingPageImageFile(slotId, file);
      processImageFileCreateMode(file, slotId);
      return;
    }
    if (!siteId) {
      alert(PAGE_MESSAGES.SITE_ID_ERROR);
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert(PAGE_MESSAGES.IMAGE_TYPE_ERROR);
      return;
    }
    if (file.size > PAGE_CONFIG.MAX_FILE_SIZE) {
      alert(PAGE_MESSAGES.FILE_SIZE_ERROR);
      return;
    }

    const localImageUrl = URL.createObjectURL(file);
    setPageSlots(prevSlots => {
      const updatedSlots = prevSlots.map(slot => {
        if (slot.id === slotId) {
          return {
            ...slot,
            page: buildPageWithUpdatedImage(slot, localImageUrl),
            isEmpty: false
          };
        }
        return slot;
      });
      return updatedSlots;
    });

    setUploadingSlots(prev => new Set(prev).add(slotId));
    uploadImage(file, siteId, slotId).then((firebaseUrl: string) => {
      // 新しい画像URLが完全に読み込まれてから切り替え
      const img = new Image();
      img.onload = () => {
        // 画像が完全に読み込まれたら切り替え
        setPageSlots(prevSlots => {
          const updatedSlots = prevSlots.map(slot => 
            slot.id === slotId && slot.page
              ? { 
                  ...slot, 
                  page: { 
                    ...slot.page, 
                    backgroundImage: firebaseUrl 
                  } 
                }
              : slot
          );
          
          return updatedSlots;
        });
        
        // アップロード完了をマーク
        setUploadingSlots(prev => {
          const newSet = new Set(prev);
          newSet.delete(slotId);
          return newSet;
        });
        
        // ローカルURLを解放
        URL.revokeObjectURL(localImageUrl);
      };
      img.src = firebaseUrl;
      
    }).catch((error: any) => {
      console.error('画像アップロードに失敗しました:', error);
      
      // エラー時はアップロード状態をクリア
      setUploadingSlots(prev => {
        const newSet = new Set(prev);
        newSet.delete(slotId);
        return newSet;
      });
      
      // エラー時はローカルURLを維持
      alert('画像のアップロードに失敗しました。再試行してください。');
    });
  };

  const updatePageImage = (pageId: string, imageUrl: string) => {
    setPageSlots(prevSlots => 
      prevSlots.map(slot => 
        slot.page?.id === pageId 
          ? { ...slot, page: { ...slot.page!, backgroundImage: imageUrl } }
          : slot
      )
    );
  };

  // 背景画像ファイル選択ハンドラー
  const handleBackgroundImageFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleBackgroundImageUpload(file);
  };

  // 背景画像アップロード処理（新規時は一時保存のみ）
  const handleBackgroundImageUpload = (file: File) => {
    if (!updateEditState) return;
    const localUrl = URL.createObjectURL(file);
    updateEditState({
      settings: {
        ...editState?.settings,
        backgroundImage: localUrl
      }
    });
    if (siteId === 'create' && onPendingBackgroundImageFile) {
      onPendingBackgroundImageFile(file);
      return;
    }
    if (!siteId) {
      alert(PAGE_MESSAGES.SITE_ID_ERROR);
      return;
    }
    uploadImage(file, siteId, 'background')
      .then((firebaseUrl: string) => {
        updateEditState({
          settings: {
            ...editState?.settings,
            backgroundImage: firebaseUrl
          }
        });
        URL.revokeObjectURL(localUrl);
      })
      .catch((error: any) => {
        console.error('背景画像のアップロードに失敗しました:', error);
        alert('背景画像のアップロードに失敗しました。');
      });
  };


  // ドラッグ&ドロップのハンドラー
  const handleDragStart = (e: React.DragEvent, slot: PageSlot) => {
    if (!slot.page) return;
    setDraggedSlot(slot);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setDraggedSlot(null);
  };

  // ドラッグ&ドロップで画像追加
  const handleDragOverImage = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDropImage = (e: React.DragEvent, slotId: string) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      processImageFile(imageFile, slotId);
    }
  };

  // 統合されたドロップハンドラー
  const handleDropUnified = (e: React.DragEvent, slot: PageSlot) => {
    e.preventDefault();
    
    // ファイルがドロップされた場合（画像アップロード）
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const imageFile = files.find(file => file.type.startsWith('image/'));
      
      if (imageFile) {
        processImageFile(imageFile, slot.id);
        return;
      }
    }
    
    // ページがドラッグされた場合（ページ移動・入れ替え）
    if (draggedSlot && draggedSlot.page) {
      setPageSlots(prevSlots => {
        const newSlots = [...prevSlots];
        const draggedIndex = newSlots.findIndex(s => s.id === draggedSlot.id);
        const targetIndex = newSlots.findIndex(s => s.id === slot.id);

        if (draggedIndex === -1 || targetIndex === -1) return prevSlots;

        const draggedPage = newSlots[draggedIndex].page;
        const targetPage = newSlots[targetIndex].page;

        // 両方にページがある場合：入れ替え
        if (draggedPage && targetPage) {
          // ドラッグされたページの位置情報をターゲット位置に更新
          const updatedDraggedPage = {
            ...draggedPage,
            row: slot.row,
            column: slot.column,
            id: `page-${slot.row}-${slot.column}`,
            pageId: `${slot.row.toString().padStart(2, '0')}-${slot.column}`
          };

          // ターゲットページの位置情報をドラッグ元位置に更新
          const updatedTargetPage = {
            ...targetPage,
            row: draggedSlot.row,
            column: draggedSlot.column,
            id: `page-${draggedSlot.row}-${draggedSlot.column}`,
            pageId: `${draggedSlot.row.toString().padStart(2, '0')}-${draggedSlot.column}`
          };

          newSlots[draggedIndex] = {
            ...newSlots[draggedIndex],
            page: updatedTargetPage,
            isEmpty: false
          };

          newSlots[targetIndex] = {
            ...newSlots[targetIndex],
            page: updatedDraggedPage,
            isEmpty: false
          };
        }
        // ドラッグ元にページがあり、ターゲットが空の場合：移動
        else if (draggedPage && !targetPage) {
          const updatedDraggedPage = {
            ...draggedPage,
            row: slot.row,
            column: slot.column,
            id: `page-${slot.row}-${slot.column}`,
            pageId: `${slot.row.toString().padStart(2, '0')}-${slot.column}`
          };

          newSlots[draggedIndex] = {
            ...newSlots[draggedIndex],
            page: undefined,
            isEmpty: true
          };

          newSlots[targetIndex] = {
            ...newSlots[targetIndex],
            page: updatedDraggedPage,
            isEmpty: false
          };
        }
        // ドラッグ元が空で、ターゲットにページがある場合：移動
        else if (!draggedPage && targetPage) {
          const updatedTargetPage = {
            ...targetPage,
            row: draggedSlot.row,
            column: draggedSlot.column,
            id: `page-${draggedSlot.row}-${draggedSlot.column}`,
            pageId: `${draggedSlot.row.toString().padStart(2, '0')}-${draggedSlot.column}`
          };

          newSlots[draggedIndex] = {
            ...newSlots[draggedIndex],
            page: updatedTargetPage,
            isEmpty: false
          };

          newSlots[targetIndex] = {
            ...newSlots[targetIndex],
            page: undefined,
            isEmpty: true
          };
        }

        return newSlots;
      });
      setDraggedSlot(null);
    }
  };

  const getSlotsByRow = (row: number) => {
    const existingSlots = pageSlots.filter(slot => slot.row === row);
    const visibleColumns = getVisibleColumns();
    
    
    // 選択された列数までのスロットを表示
    const result = [];
    for (const column of visibleColumns) {
      const existingSlot = existingSlots.find(slot => slot.column === column);
      if (existingSlot) {
        result.push(existingSlot);
      } else {
        // 空のスロットを作成
        result.push({
        id: `${row}-${column}`,
        row,
        column: column as 'A' | 'B' | 'C' | 'D',
        page: undefined,
        isEmpty: true
        });
      }
    }
    
    return result;
  };

  // 表示する行を取得（実際の行数まで）
  const getVisibleRows = () => {
    return Array.from({ length: editState?.maxRows || 1 }, (_, i) => i + 1);
  };

  // 表示する列を取得（実際の列数まで）
  const getVisibleColumns = () => {
    const maxColumns = editState?.maxColumns || 1;
    const columns = [];
    for (let i = 0; i < maxColumns; i++) {
      columns.push(getColumnLetter(i + 1));
    }
    return columns;
  };

  return (
    <Box sx={{ p: 2, pb: 12 }}>
      {/* 画像ライブラリモーダル（ページ画像用）※保存済みサイトのみ */}
      {siteId && siteId !== 'create' && (
        <ImageLibraryModal
          open={pageImageLibraryModalOpen}
          onClose={() => {
            setPageImageLibraryModalOpen(false);
            setPageImageMenuAnchor(null);
            setSelectedSlotId(null);
          }}
          onSelect={handlePageImageSelected}
          images={libraryImages}
          videos={libraryVideos}
          title="ページ画像を選択"
          usedImageUrls={getUsedImageUrls()}
          loading={libraryImagesLoading}
        />
      )}
      
      {/* ページ画像選択メニュー（新規時も表示） */}
      {siteId && (
        <>
          <Menu
            anchorEl={pageImageMenuAnchor}
            open={Boolean(pageImageMenuAnchor)}
            onClose={() => {
              setPageImageMenuAnchor(null);
              setSelectedSlotId(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', mt: 0.5, minWidth: 180 } }}
          >
            <MenuItem onClick={() => { setPageImageMenuAnchor(null); pageImageFileInputRef.current?.click(); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><UploadIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              デバイスから選択
            </MenuItem>
            <MenuItem onClick={() => { setPageImageMenuAnchor(null); if (siteId !== 'create') setPageImageLibraryModalOpen(true); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><LibraryIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              ライブラリから選択
            </MenuItem>
          </Menu>
          <input type="file" ref={pageImageFileInputRef} accept="image/*" style={{ display: 'none' }} onChange={handlePageImageFileSelect} />
        </>
      )}
      
      {/* 画像選択ダイアログ（背景画像用）※保存済みサイトのみ */}
      {siteId && siteId !== 'create' && (
        <ImageLibraryModal
          open={backgroundImageLibraryModalOpen}
          onClose={() => setBackgroundImageLibraryModalOpen(false)}
          onSelect={handleBackgroundImageSelected}
          images={libraryImages}
          videos={libraryVideos}
          title="背景画像を選択"
          usedImageUrls={getUsedImageUrls()}
          loading={libraryImagesLoading}
        />
      )}
      
      {/* 背景画像選択メニュー（新規時も表示） */}
      {siteId && (
        <>
          <Menu
            anchorEl={backgroundImageMenuAnchor}
            open={Boolean(backgroundImageMenuAnchor)}
            onClose={() => setBackgroundImageMenuAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                mt: 0.5,
                minWidth: 180,
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setBackgroundImageMenuAnchor(null);
                backgroundImageFileInputRef.current?.click();
              }}
              sx={{ py: 1.5, px: 2 }}
            >
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                <UploadIcon sx={{ fontSize: '18px', color: '#616161' }} />
              </Box>
              デバイスから選択
            </MenuItem>
            <MenuItem
              onClick={() => {
                setBackgroundImageMenuAnchor(null);
                if (siteId !== 'create') setBackgroundImageLibraryModalOpen(true);
              }}
              sx={{ py: 1.5, px: 2 }}
            >
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                <LibraryIcon sx={{ fontSize: '18px', color: '#616161' }} />
              </Box>
              ライブラリから選択
            </MenuItem>
          </Menu>
          <input
            ref={backgroundImageFileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleBackgroundImageFileSelect}
            style={{ display: 'none' }}
          />
        </>
      )}
      
      {/* ページ設定 */}
      <GridSizeControls
        tempRow={tempRow}
        tempColumn={tempColumn}
        onTempRowChange={setTempRow}
        onTempColumnChange={setTempColumn}
        onApplyGridSize={handleApplyGridSize}
        onClearAll={handleClearAll}
      />

      {/* バリデーションエラー表示 */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* グリッドレイアウト */}
      <PageGrid
        visibleRows={getVisibleRows()}
        visibleColumns={getVisibleColumns()}
        getSlotsByRow={getSlotsByRow}
        uploadingSlots={uploadingSlots}
        draggedSlot={draggedSlot}
        onDragStart={handleDragStart}
        onDrop={handleDropUnified}
        onDragEnd={handleDragEnd}
        onDeletePage={handleDeletePage}
        onEditPage={(page) => {
          setEditingPage(page);
          // editingPageが設定された後にpageDialogOpenをtrueにする
          setTimeout(() => {
            setPageDialogOpen(true);
          }, 0);
        }}
        onImageSelect={handleImageSelect}
        onImageDrop={handleDropImage}
        onImageDragOver={handleDragOverImage}
        onAddRow={handleAddRow}
        onAddColumn={handleAddColumn}
        maxRows={editState?.maxRows}
        maxColumns={editState?.maxColumns}
      />

      {/* 背景設定 */}
      <Box sx={{ mt: 4 }}>
        <BackgroundSettingsSection
        backgroundImage={editState?.settings?.backgroundImage}
        backgroundColor={editState?.settings?.backgroundColor}
        pageBackgroundColor={editState?.settings?.pageBackgroundColor}
        onBackgroundImageMenuClick={(e) => {
          if (siteId) {
            setBackgroundImageMenuAnchor(e.currentTarget);
          }
        }}
        onBackgroundImageDrop={handleBackgroundImageUpload}
        onBackgroundImageDelete={() => {
          if (updateEditState) {
            updateEditState({
              settings: {
                ...editState?.settings,
                backgroundImage: undefined
              }
            });
          }
        }}
        onBackgroundColorChange={(color) => {
          if (updateEditState) {
            updateEditState({
              settings: {
                ...editState?.settings,
                backgroundColor: color
              }
            });
          }
        }}
        onPageBackgroundColorChange={(color) => {
          if (updateEditState) {
            updateEditState({
              settings: {
                ...editState?.settings,
                pageBackgroundColor: color
              }
            });
          }
        }}
      />
      </Box>

      {/* ページ編集ダイアログ */}
      <PageEditDialog
        key={editingPage?.id || 'new'}
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        page={editingPage}
        onSave={(pageData) => {
          // ページデータを更新
          setPageSlots(prevSlots => 
            prevSlots.map(slot => 
              slot.page?.id === editingPage?.id 
                ? { ...slot, page: { ...slot.page!, ...pageData } }
                : slot
            )
          );
          
          // editStateのpagesも更新
          if (updateEditState && editState) {
            const currentPages = editState.pages || [];
            const updatedPages = currentPages.map((p: Page) => {
              if (p.id === editingPage?.id) {
                return {
                  ...p,
                  ...pageData
                };
              }
              return p;
            });
            
            updateEditState({
              pages: updatedPages
            });
          }
          
          setPageDialogOpen(false);
        }}
        siteId={siteId}
        editState={editState}
        updateEditState={updateEditState}
        updateSite={updateSite}
        updateAppliedContent={updateAppliedContent}
        getAppliedContent={getAppliedContent}
        cachedImages={cachedImages}
        preloaded={preloaded}
        onPendingSlideImageFile={onPendingSlideImageFile}
      />

      {/* 警告ダイアログ */}
      <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
        <DialogTitle>警告</DialogTitle>
        <DialogContent>
          <Typography>
            グリッドサイズを縮小すると、既存のページが削除されます。
            続行しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWarning(false)}>キャンセル</Button>
          <Button onClick={handleForceApply} color="error" variant="contained">
            削除して適用
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSlotToDelete(null);
        }}
        onConfirm={confirmDeletePage}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />
    </Box>
  );
};
